import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FileText,
  Pencil,
  Trash2,
  Lock,
  MessageSquare,
  Scale,
} from "lucide-react";
import Navigation from "@/components/shared/Navigation";
import Footer from "@/components/shared/Footer";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const [activeTab, setActiveTab] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  const scrollToSection = (sectionId) => {
    navigate(`/?section=${sectionId}`);
  };

  return (
    <div>
      <Navigation
        onSectionClick={scrollToSection}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      <div className="privacy-page" style={{ paddingTop: "65px" }}>
        {/* HERO */}
        <section className="hero">
          <div className="hero-inner">
            <p className="hero-eyebrow">Legal &amp; Privacy</p>
            <h1>Privacy Policy</h1>
            <div className="hero-meta">
              <span>
                <strong>Effective date:</strong> 16 June 2026
              </span>
              <span>
                <strong>Last updated:</strong> 16 June 2026
              </span>
              <span>
                <strong>Version:</strong> 1.0
              </span>
            </div>
          </div>
        </section>

        {/* PAGE BODY */}
        <div className="page-body">
          {/* SIDEBAR */}
          <aside className="sidebar">
            <p className="sidebar-label">On this page</p>
            <ul className="toc">
              <li>
                <a href="#who-we-are">1. Who we are</a>
              </li>
              <li>
                <a href="#what-we-collect">2. What we collect</a>
              </li>
              <li>
                <a href="#why-we-use">3. Why we use it</a>
              </li>
              <li>
                <a href="#who-we-share">4. Who we share it with</a>
              </li>
              <li>
                <a href="#cookies">5. Cookies &amp; tracking</a>
              </li>
              <li>
                <a href="#partners">6. Third-party partners</a>
              </li>
              <li>
                <a href="#ai-features">7. AI features</a>
              </li>
              <li>
                <a href="#hms">8. HMS (Hospitals)</a>
              </li>
              <li>
                <a href="#abha">9. ABHA &amp; national health network</a>
              </li>
              <li>
                <a href="#retention">10. How long we keep it</a>
              </li>
              <li>
                <a href="#your-rights">11. Your rights</a>
              </li>
              <li>
                <a href="#children">12. Children</a>
              </li>
              <li>
                <a href="#security">13. Security</a>
              </li>
              <li>
                <a href="#changes">14. Changes to this policy</a>
              </li>
              <li>
                <a href="#contact">15. Contact &amp; grievances</a>
              </li>
            </ul>
          </aside>

          {/* MAIN CONTENT */}
          <main className="content">
            <div className="summary-box">
              <p>
                <strong>Plain-language summary:</strong> HealthismPlus collects
                health and personal data to help you book doctors, order
                medicines, book lab tests, and use AI health tools. We share
                only what's needed with doctors, labs, and pharmacies to fulfil
                your requests. We never sell your data. You can withdraw
                consent, correct your data, or ask us to delete it at any time —
                from your account settings or by writing to us.
              </p>
            </div>

            {/* SECTION 1 */}
            <section className="policy-section" id="who-we-are">
              <p className="section-number">Section 01</p>
              <h2>Who we are and how this policy applies</h2>
              <p>
                This Privacy Policy is published by{" "}
                <strong>Healthismplus Technologies Private Limited</strong>,
                operating as <strong>HealthismPlus</strong> ("HealthismPlus",
                "we", "our", or "us"), a company incorporated under the
                Companies Act, 2013, with its registered office at{" "}
                <strong>
                  405 Garnet Palladium, Behind Express Zone, Motilal Nehru Road,
                  Goregaon East, Mumbai – 400063, Maharashtra, India
                </strong>
                .
              </p>
              <p>This policy applies to:</p>
              <p>
                → The <strong>HealthismPlus app and website</strong>{" "}
                (healthismplus.com), a consumer healthcare marketplace offering
                doctor booking, medicine ordering, lab test booking, and AI
                wellness tools ("the App").
              </p>
              <p>
                → The <strong>HealthismPlus HMS</strong>, a hospital and clinic
                management software used by healthcare providers to manage
                appointments, consultations, patient health records, TPA
                processing, and clinic operations ("HMS").
              </p>
              <p>
                By using either product, you agree to the practices described in
                this policy. If you do not agree, please stop using the relevant
                product and contact us to request deletion of your data.
              </p>
              <p>
                This policy is prepared in compliance with the{" "}
                <strong>
                  Digital Personal Data Protection Act, 2023 (DPDP Act)
                </strong>
                , the <strong>DPDP Rules, 2025</strong>, the{" "}
                <strong>Information Technology (Amendment) Act, 2008</strong>,
                the <strong>Telemedicine Practice Guidelines, 2020</strong>{" "}
                issued by the Ministry of Health and Family Welfare, and the{" "}
                <strong>
                  Ayushman Bharat Digital Mission (ABDM) Health Data Management
                  Policy
                </strong>{" "}
                issued by the National Health Authority.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 2 */}
            <section className="policy-section" id="what-we-collect">
              <p className="section-number">Section 02</p>
              <h2>What personal data we collect</h2>
              <p>
                We collect different types of data depending on which feature
                you use. The table below maps data to the specific product
                feature that collects it.
              </p>

              <h3>A. Account &amp; Identity</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Collected when</th>
                    <th>Required?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Full name, mobile number, email address</td>
                    <td>Account registration</td>
                    <td>
                      <span className="tag tag-req">Required</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Date of birth, biological sex</td>
                    <td>Profile setup</td>
                    <td>
                      <span className="tag tag-req">Required</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Profile photo</td>
                    <td>Profile setup</td>
                    <td>
                      <span className="tag tag-opt">Optional</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Address (for home delivery)</td>
                    <td>Medicine/lab orders</td>
                    <td>
                      <span className="tag tag-req">Required for delivery</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3>B. Health &amp; Medical Data</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Collected when</th>
                    <th>Required?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Prescriptions (uploaded by you)</td>
                    <td>Medicine order / Health Locker</td>
                    <td>
                      <span className="tag tag-req">
                        Required for medicine orders
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Lab reports and test results (uploaded by lab/doctor)
                    </td>
                    <td>Health Locker</td>
                    <td>
                      <span className="tag tag-opt">Optional</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Medical history, symptoms, chief complaints</td>
                    <td>Teleconsultation booking form</td>
                    <td>
                      <span className="tag tag-opt">Optional</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Food diary entries (via Calorify)</td>
                    <td>Calorify AI feature</td>
                    <td>
                      <span className="tag tag-opt">Optional</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Food images</td>
                    <td>Calorify photo capture</td>
                    <td>
                      <span className="tag tag-opt">Optional</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Dietary preferences, goals, health conditions</td>
                    <td>AI Dietician onboarding</td>
                    <td>
                      <span className="tag tag-opt">
                        Optional — needed for AI personalisation
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3>C. Transactional &amp; Usage Data</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Collected when</th>
                    <th>Required?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Doctor booking history, appointment records</td>
                    <td>Booking confirmation</td>
                    <td>
                      <span className="tag tag-req">Required</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Medicine order history, order status</td>
                    <td>Medicine order placement</td>
                    <td>
                      <span className="tag tag-req">Required</span>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Lab test bookings, collection slot, home/visit preference
                    </td>
                    <td>Lab booking</td>
                    <td>
                      <span className="tag tag-req">Required</span>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Payment information (processed via{" "}
                      <strong>Razorpay</strong> — card number, UPI, net banking
                      details are handled entirely by Razorpay and never stored
                      on our servers)
                    </td>
                    <td>Checkout</td>
                    <td>
                      <span className="tag tag-req">Required</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3>D. Device &amp; Technical Data</h3>
              <p>
                We collect device type, operating system, app version, IP
                address, and crash logs to keep the app running securely. We do
                not use this data to profile you for advertising.
              </p>

              <div className="notice-box">
                <p>
                  <strong>Note on sensitive personal data:</strong> Health data,
                  medical records, prescriptions, and food/nutritional
                  information are classified as <em>sensitive personal data</em>{" "}
                  under the Information Technology (Reasonable Security
                  Practices and Procedures and Sensitive Personal Data or
                  Information) Rules, 2011. We apply the highest level of
                  protection to this data and will never use it for advertising,
                  insurance underwriting, or any purpose not explicitly
                  described here.
                </p>
              </div>
            </section>

            <hr className="section-rule" />

            {/* SECTION 3 */}
            <section className="policy-section" id="why-we-use">
              <p className="section-number">Section 03</p>
              <h2>Why we use your data</h2>
              <p>
                We only use your data for the purposes you consented to, or
                where we have a lawful basis under the DPDP Act. We do not use
                your data for any purpose not listed here without obtaining
                fresh consent.
              </p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Lawful basis</th>
                    <th>Feature</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Create and manage your account</td>
                    <td>Consent + contract fulfilment</td>
                    <td>All features</td>
                  </tr>
                  <tr>
                    <td>Connect you with a doctor for booking</td>
                    <td>Consent</td>
                    <td>Doctor booking</td>
                  </tr>
                  <tr>
                    <td>
                      Share your prescription with our pharmacy partner to
                      process your medicine order
                    </td>
                    <td>Consent</td>
                    <td>Medicine orders</td>
                  </tr>
                  <tr>
                    <td>
                      Share your booking details with a lab to arrange home
                      collection or walk-in
                    </td>
                    <td>Consent</td>
                    <td>Lab booking</td>
                  </tr>
                  <tr>
                    <td>
                      Store prescriptions and reports in your Health Locker
                    </td>
                    <td>Consent</td>
                    <td>Health Locker</td>
                  </tr>
                  <tr>
                    <td>
                      Analyse food images to provide calorie and nutritional
                      estimates
                    </td>
                    <td>Consent (separate, optional)</td>
                    <td>Calorify</td>
                  </tr>
                  <tr>
                    <td>Provide personalised diet recommendations</td>
                    <td>Consent (separate, optional)</td>
                    <td>AI Dietician</td>
                  </tr>
                  <tr>
                    <td>Process payments and maintain transaction records</td>
                    <td>Contract + legal obligation</td>
                    <td>All paid features</td>
                  </tr>
                  <tr>
                    <td>
                      Send service notifications (booking confirmation, order
                      status)
                    </td>
                    <td>Consent + contract</td>
                    <td>All features</td>
                  </tr>
                  <tr>
                    <td>Send health tips, promotional offers</td>
                    <td>
                      Consent (separate, optional — you can opt out anytime)
                    </td>
                    <td>Marketing</td>
                  </tr>
                  <tr>
                    <td>
                      Improve app features using anonymised, aggregated usage
                      patterns
                    </td>
                    <td>Consent (optional)</td>
                    <td>Analytics</td>
                  </tr>
                  <tr>
                    <td>Comply with legal and regulatory requirements</td>
                    <td>Legal obligation</td>
                    <td>All features</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <hr className="section-rule" />

            {/* SECTION 4 */}
            <section className="policy-section" id="who-we-share">
              <p className="section-number">Section 04</p>
              <h2>Who we share your data with</h2>
              <p>
                We share your data only to the extent necessary to fulfil the
                service you requested. We never sell your personal data.
              </p>

              <h3>Service delivery partners</h3>
              <p>
                <strong>Doctors and specialists:</strong> When you book a
                teleconsultation, we share your name, contact number, and (if
                provided) your chief complaint with the relevant registered
                medical practitioner so they can reach you. The consultation
                itself takes place outside our platform. The doctor's privacy
                practices are governed by their own policies and the
                Telemedicine Practice Guidelines, 2020.
              </p>
              <p>
                <strong>Pharmacy partners:</strong> When you place a medicine
                order, we share your name, delivery address, uploaded
                prescription, and order details with our pharmacy partner(s) to
                process and deliver your order. Our pharmacy partners are
                independent Data Fiduciaries for the data they collect from you
                directly during fulfilment.
              </p>
              <p>
                <strong>Diagnostic lab partners:</strong> When you book a lab
                test, we share your name, contact, address, and the test
                requested with the relevant lab to arrange collection or
                appointment. Our lab partners are independent Data Fiduciaries
                for the data they handle independently.
              </p>

              <h3>Technology and infrastructure</h3>
              <p>
                Our primary backend and database are hosted on{" "}
                <strong>e2e Networks</strong>, an Indian cloud provider with
                data centres in India. Your core personal data does not leave
                India. Key third-party technology services we use:
              </p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Data leaves India?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Primary hosting &amp; database</td>
                    <td>e2e Networks</td>
                    <td>Core app backend, user data storage</td>
                    <td>No — Indian servers</td>
                  </tr>
                  <tr>
                    <td>Authentication &amp; push notifications</td>
                    <td>Google Firebase (Google LLC)</td>
                    <td>OTP sign-in, FCM push notifications</td>
                    <td>Possibly — Google global infra</td>
                  </tr>
                  <tr>
                    <td>Crash reporting &amp; analytics</td>
                    <td>Firebase Crashlytics / Analytics (Google LLC)</td>
                    <td>Anonymous crash logs, usage stats</td>
                    <td>Possibly — Google global infra</td>
                  </tr>
                  <tr>
                    <td>Payment processing</td>
                    <td>Razorpay</td>
                    <td>
                      Card, UPI, net banking payments — card data never touches
                      our servers
                    </td>
                    <td>No — Razorpay is India-based</td>
                  </tr>
                  <tr>
                    <td>Maps &amp; location</td>
                    <td>Google Maps SDK (Google LLC)</td>
                    <td>Show nearby doctors, labs, pharmacies on map</td>
                    <td>Possibly — Google global infra</td>
                  </tr>
                </tbody>
              </table>

              <h3>Legal and regulatory disclosure</h3>
              <p>
                We may disclose your data to law enforcement, courts, or
                regulators when required by law or to protect the rights,
                property, or safety of our users or the public.
              </p>

              <div className="notice-box">
                <p>
                  <strong>Important:</strong> When you interact with a pharmacy
                  partner or lab partner's staff or systems outside the
                  HealthismPlus app, those interactions are governed by the
                  partner's own privacy policy. We recommend reviewing those
                  policies before sharing additional data with them directly.
                </p>
              </div>
            </section>

            <hr className="section-rule" />

            {/* SECTION 5 */}
            <section className="policy-section" id="cookies">
              <p className="section-number">Section 05</p>
              <h2>Cookies &amp; tracking technologies</h2>
              <p>
                HealthismPlus uses cookies and similar tracking technologies on
                its website (<strong>www.healthismplus.com</strong>) and mobile
                application. A cookie is a small data file stored on your
                browser or device. You can configure your browser to accept,
                reject, or notify you when a cookie is set; however, blocking
                essential cookies may prevent sign-in or personalised features
                from working.
              </p>

              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Purpose</th>
                    <th>Tools / Examples</th>
                    <th>Can you opt out?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>Essential</strong>
                    </td>
                    <td>
                      Required for the service to function — authentication,
                      session management, security (CSRF)
                    </td>
                    <td>Session tokens, CSRF cookies</td>
                    <td>No — these are required</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Analytics</strong>
                    </td>
                    <td>
                      Understand how users navigate the app and website to
                      improve performance and fix issues
                    </td>
                    <td>
                      Firebase Analytics, Firebase Crashlytics (Google LLC)
                    </td>
                    <td>Yes — opt out in Settings → Privacy → Analytics</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Functional</strong>
                    </td>
                    <td>
                      Remember your preferences such as language, location, and
                      notification settings
                    </td>
                    <td>Local storage / preference cookies</td>
                    <td>
                      Yes — clearing app data or browser cookies removes these
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3>Firebase Analytics &amp; Crashlytics</h3>
              <p>
                We use <strong>Google Firebase Analytics</strong> to collect
                anonymous, aggregated usage statistics (pages visited, feature
                usage, session length) and <strong>Firebase Crashlytics</strong>{" "}
                to automatically capture crash reports when the app encounters
                an error. Neither tool is used to profile individual users for
                advertising. Firebase is operated by Google LLC and is governed
                by{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google's Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://firebase.google.com/support/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Firebase's data security terms
                </a>
                .
              </p>

              <h3>No advertising or third-party ad cookies</h3>
              <p>
                We do <strong>not</strong> run advertising networks, ad
                retargeting pixels, or third-party tracking cookies (such as
                Meta Pixel, Google Ads, etc.) on www.healthismplus.com or within
                the HealthismPlus app. Your browsing and health activity on our
                platform is never used for ad targeting.
              </p>

              <div className="notice-box">
                <p>
                  <strong>Cross-border note:</strong> Firebase Analytics and
                  Crashlytics may transfer anonymised technical data to Google
                  servers outside India. This transfer covers only crash logs
                  and aggregated usage statistics — not your personal health
                  data, which stays on our Indian servers (e2e Networks).
                </p>
              </div>
            </section>

            <hr className="section-rule" />

            {/* SECTION 6 */}
            <section className="policy-section" id="partners">
              <p className="section-number">Section 06</p>
              <h2>Third-party partners &amp; independent data controllers</h2>
              <p>
                HealthismPlus operates as a marketplace. Some parties you
                interact with through our platform — specifically our partner
                pharmacies and diagnostic labs — independently collect and
                control data they receive from you during fulfilment. Under the
                DPDP Act, 2023, they are independent Data Fiduciaries for that
                data, not Data Processors acting on our behalf.
              </p>
              <p>This means:</p>
              <p>
                → Their data practices for the data they independently collect
                are governed by their own privacy notices.
              </p>
              <p>
                → You should direct data correction, deletion, or
                consent-related requests for data held solely by them directly
                to them.
              </p>
              <p>
                → HealthismPlus is responsible only for the data we collect and
                process directly on our systems.
              </p>
              <p>
                We contractually require all partners to comply with applicable
                Indian data protection laws as a condition of being listed on
                our platform.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 7 */}
            <section className="policy-section" id="ai-features">
              <p className="section-number">Section 07</p>
              <h2>AI features — Calorify &amp; AI Dietician</h2>
              <p>
                Our AI-powered features process sensitive health and nutritional
                data. Here is exactly how each one works.
              </p>

              <h3>Calorify</h3>
              <p>
                When you take or upload a food photo, the image is sent to our
                AI model to identify the food and estimate its calorie, protein,
                fat, and carbohydrate content. The results are saved to your
                food diary. Images are processed securely and not used to train
                models on an individually identifiable basis without your
                explicit separate consent.
              </p>

              <h3>AI Dietician</h3>
              <p>
                If you use the AI Dietician, you may provide information about
                your health goals, dietary restrictions, allergies, and existing
                medical conditions. This data is used solely to generate
                personalised recommendations for you. It is not shared with any
                third party for commercial purposes.
              </p>

              <div className="notice-box">
                <p>
                  <strong>Disclaimer:</strong> AI Dietician recommendations and
                  Calorify estimates are for informational and wellness purposes
                  only. They do not constitute medical advice, diagnosis, or
                  treatment. Always consult a qualified healthcare provider
                  before making changes to your diet based on a health
                  condition.
                </p>
              </div>

              <p>
                You can delete all AI feature data — your food diary, calorie
                logs, and dietician profile — at any time from{" "}
                <strong>Settings → Health Data → Delete AI data</strong>.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 8 */}
            <section className="policy-section" id="hms">
              <p className="section-number">Section 08</p>
              <h2>HealthismPlus HMS — for hospital and clinic users</h2>
              <p>
                The HealthismPlus HMS is a software product used by hospitals,
                clinics, and individual practitioners ("Healthcare Providers")
                to manage patient appointments, consultations, electronic health
                records (EHR), TPA and insurance workflows, and clinic
                operations.
              </p>

              <h3>Who is the Data Fiduciary for HMS patient data?</h3>
              <p>
                For patient data entered into and managed through HMS, the
                Healthcare Provider (hospital or clinic) is the Data Fiduciary —
                they determine the purposes for which patient data is collected.
                HealthismPlus acts as a Data Processor, storing and processing
                this data on our infrastructure on the Healthcare Provider's
                instructions.
              </p>
              <p>
                Healthism's responsibilities in respect of HMS patient data
                include: ensuring data security and encryption at rest and in
                transit; enforcing access controls; enabling data portability
                and deletion on instruction; and complying with applicable law.
              </p>

              <h3>What HMS data HealthismPlus holds</h3>
              <p>
                Patient names, contact details, appointment records,
                consultation notes, diagnoses, prescriptions, investigation
                reports, TPA and insurance information, and any other clinical
                data entered by the Healthcare Provider's staff. This data is
                held on behalf of the Healthcare Provider and is not used by
                HealthismPlus for any purpose beyond operating and improving the
                HMS product.
              </p>

              <h3>Pediatric patients and dependent profiles</h3>
              <p>
                Healthcare Providers using HMS may register minors as patients,
                including through a parent or guardian's dependent profile for
                pediatric bookings, consultations, or health records. In such
                cases, the Healthcare Provider is responsible for obtaining
                verifiable parental or guardian consent, consistent with
                applicable state Clinical Establishment regulations and NMC
                (National Medical Commission) professional conduct norms. We do
                not use a minor's data for tracking, behavioural monitoring, or
                targeted advertising.
              </p>

              <h3>Data requests from HMS patients</h3>
              <p>
                If you are a patient whose data is held in HMS by a hospital or
                clinic, please direct data access, correction, or deletion
                requests first to the Healthcare Provider (hospital or clinic).
                If your Healthcare Provider asks us to action a request on their
                behalf, we will do so promptly. You may also contact us directly
                at{" "}
                <a href="mailto:info@healthismplus.com">
                  info@healthismplus.com
                </a>
                .
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 9 */}
            <section className="policy-section" id="abha">
              <p className="section-number">Section 09</p>
              <h2>ABHA &amp; the Ayushman Bharat Digital Mission</h2>
              <p>
                HealthismPlus is integrating with the{" "}
                <strong>Ayushman Bharat Digital Mission (ABDM)</strong>, India's
                national digital health infrastructure operated by the National
                Health Authority (NHA). As part of this, users can create or
                link their{" "}
                <strong>ABHA (Ayushman Bharat Health Account)</strong> — a
                unique health ID that allows your health records to be stored,
                accessed, and shared securely across ABDM-connected providers.
              </p>

              <div className="notice-box">
                <p>
                  <strong>ABHA linking is entirely optional.</strong> You can
                  use HealthismPlus in full without creating or linking an ABHA.
                  Choosing not to link an ABHA has no impact on your access to
                  doctor bookings, medicine orders, lab tests, or AI features.
                </p>
              </div>

              <h3>What roles HealthismPlus plays in the ABDM network</h3>
              <p>
                Within the ABDM framework, HealthismPlus operates in the
                following capacities:
              </p>
              <p>
                <strong>Health Information Provider (HIP):</strong> When a
                doctor or lab uploads a prescription or report to your Health
                Locker through our platform or HMS, HealthismPlus can, with your
                consent, push that record to your ABHA Health Locker so it is
                available to you and authorised providers across the ABDM
                network.
              </p>
              <p>
                <strong>Health Information User (HIU):</strong> When a
                healthcare provider using HMS needs to view your health records
                held by another ABDM-connected provider (for example, a prior
                prescription from a different hospital), HealthismPlus — acting
                as HIU — will send a time-bound consent request to your ABHA
                Mobile Application. Your records are retrieved only after you
                explicitly approve that request.
              </p>

              <h3>What happens when you create or link an ABHA</h3>
              <p>
                When you create a new ABHA or link an existing one through our
                app, we use the NHA's official ABHA Registration API. The
                following data is shared with the NHA at that point: your name,
                date of birth, mobile number, and (if you choose Aadhaar-based
                verification) your Aadhaar details. This data is handled by the
                NHA under their own Privacy Policy, available at{" "}
                <a
                  href="https://abdm.gov.in"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  abdm.gov.in
                </a>
                .
              </p>
              <p>
                Once linked, your ABHA ID (a 14-digit number) is stored in your
                HealthismPlus profile and used as your identifier within the
                ABDM network. We do not store your Aadhaar number.
              </p>

              <h3>ABDM consent — how it works</h3>
              <p>
                Every health record shared over the ABDM network requires a
                separate, explicit, time-bound consent from you. This is above
                and beyond the consents you give HealthismPlus directly.
              </p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Where consent happens</th>
                    <th>What you are approving</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Pushing a record to your ABHA Health Locker</td>
                    <td>In-app prompt (Healthism)</td>
                    <td>
                      Sending a specific prescription or report to your national
                      ABHA locker
                    </td>
                  </tr>
                  <tr>
                    <td>
                      A provider requesting your records from another hospital
                    </td>
                    <td>Your ABHA Mobile App (outside Healthism)</td>
                    <td>
                      Approving a time-bound request to share specific records
                      with a named provider
                    </td>
                  </tr>
                  <tr>
                    <td>Revoking a previously approved data request</td>
                    <td>
                      Your ABHA Mobile App or HealthismPlus Consent dashboard
                    </td>
                    <td>
                      Withdrawing an active data-sharing consent; data access
                      stops immediately
                    </td>
                  </tr>
                </tbody>
              </table>
              <p>
                You can view all active and past ABDM consent requests —
                including who requested your data, what was shared, and for how
                long — from{" "}
                <strong>
                  Settings → Privacy &amp; Data → ABHA Consent History
                </strong>
                .
              </p>

              <h3>Data that flows to the ABDM network</h3>
              <p>
                Only records you explicitly approve are pushed to the ABDM
                network. These records are transmitted in{" "}
                <strong>FHIR R4 format</strong> (the national standard for
                clinical data interoperability) over encrypted channels. Once a
                record is in your ABHA locker, it is under the governance of the
                NHA's Health Data Management Policy, in addition to this Privacy
                Policy.
              </p>

              <h3>What HealthismPlus does not do with ABHA data</h3>
              <p>
                We do not use ABHA-linked health records for advertising,
                insurance profiling, or any commercial purpose. We do not access
                records in your ABHA locker except when a consent request you
                approved is active. We do not share your ABHA ID or linked
                records with any party outside of ABDM-compliant data exchange
                flows.
              </p>

              <h3>Unlinking your ABHA</h3>
              <p>
                You can unlink your ABHA from your HealthismPlus account at any
                time from <strong>Settings → Profile → ABHA → Unlink</strong>.
                Unlinking removes the association between your ABHA and your
                HealthismPlus account. It does not delete records already in
                your national ABHA locker — to manage those, use the ABHA Mobile
                Application or visit abdm.gov.in.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 10 */}
            <section className="policy-section" id="retention">
              <p className="section-number">Section 10</p>
              <h2>How long we keep your data</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data type</th>
                    <th>Retention period</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Account and profile data</td>
                    <td>Until account deletion + 30 days</td>
                    <td>Active service delivery</td>
                  </tr>
                  <tr>
                    <td>Prescriptions &amp; lab reports (Health Locker)</td>
                    <td>Until you delete them, or account deletion</td>
                    <td>User-controlled health records</td>
                  </tr>
                  <tr>
                    <td>Medicine order history</td>
                    <td>5 years from order date</td>
                    <td>Legal, consumer protection</td>
                  </tr>
                  <tr>
                    <td>Lab test booking records</td>
                    <td>3 years from booking date</td>
                    <td>Legal, dispute resolution</td>
                  </tr>
                  <tr>
                    <td>Payment and invoicing records</td>
                    <td>7 years from transaction</td>
                    <td>Income Tax Act, GST obligations</td>
                  </tr>
                  <tr>
                    <td>Calorify food diary data</td>
                    <td>Until deleted by user or account deletion</td>
                    <td>User-controlled</td>
                  </tr>
                  <tr>
                    <td>AI Dietician profile</td>
                    <td>Until deleted by user or account deletion</td>
                    <td>User-controlled</td>
                  </tr>
                  <tr>
                    <td>Consent records</td>
                    <td>3 years post-withdrawal or account deletion</td>
                    <td>Internal audit and recordkeeping policy</td>
                  </tr>
                  <tr>
                    <td>HMS patient records</td>
                    <td>
                      As instructed by Healthcare Provider; minimum 3 years
                    </td>
                    <td>
                      Applicable state Clinical Establishment regulations and/or
                      NMC (National Medical Commission) professional conduct
                      regulations
                    </td>
                  </tr>
                  <tr>
                    <td>ABHA ID (linked)</td>
                    <td>Until you unlink ABHA or delete account</td>
                    <td>User-controlled ABDM integration</td>
                  </tr>
                  <tr>
                    <td>ABDM consent records (approvals, revocations)</td>
                    <td>3 years from consent action date</td>
                    <td>
                      ABDM Health Data Management Policy audit requirements
                    </td>
                  </tr>
                </tbody>
              </table>
              <p>
                When data is due for deletion, we delete or irreversibly
                anonymise it within 30 days of the retention period ending.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 11 */}
            <section className="policy-section" id="your-rights">
              <p className="section-number">Section 11</p>
              <h2>Your rights under the DPDP Act, 2023</h2>
              <p>
                The DPDP Act gives you the following rights over your personal
                data. You can exercise most of these directly in the app under{" "}
                <strong>Settings → Privacy &amp; Data</strong>.
              </p>

              <div className="rights-grid">
                <div className="right-card">
                  <span className="right-icon">
                    <FileText size={22} strokeWidth={1.75} />
                  </span>
                  <strong>Right to access</strong>
                  <p>
                    Request a summary of what personal data we hold about you
                    and how we use it.
                  </p>
                </div>
                <div className="right-card">
                  <span className="right-icon">
                    <Pencil size={22} strokeWidth={1.75} />
                  </span>
                  <strong>Right to correction</strong>
                  <p>
                    Ask us to correct any inaccurate or incomplete personal
                    data.
                  </p>
                </div>
                <div className="right-card">
                  <span className="right-icon">
                    <Trash2 size={22} strokeWidth={1.75} />
                  </span>
                  <strong>Right to erasure</strong>
                  <p>
                    Request deletion of your data when you close your account or
                    withdraw consent. Deletion within 30 days.
                  </p>
                </div>
                <div className="right-card">
                  <span className="right-icon">
                    <Lock size={22} strokeWidth={1.75} />
                  </span>
                  <strong>Right to withdraw consent</strong>
                  <p>
                    Withdraw any consent you gave, at any time, as easily as you
                    gave it. From Settings → Privacy.
                  </p>
                </div>
                <div className="right-card">
                  <span className="right-icon">
                    <MessageSquare size={22} strokeWidth={1.75} />
                  </span>
                  <strong>Right to grievance redressal</strong>
                  <p>
                    Raise a complaint with our Grievance Officer. We will
                    respond within 7 business days.
                  </p>
                </div>
                <div className="right-card">
                  <span className="right-icon">
                    <Scale size={22} strokeWidth={1.75} />
                  </span>
                  <strong>Right to escalate</strong>
                  <p>
                    If unsatisfied with our response, escalate to the Data
                    Protection Board of India.
                  </p>
                </div>
              </div>

              <h3>How to withdraw consent</h3>
              <p>
                Go to{" "}
                <strong>Settings → Privacy &amp; Data → Manage Consents</strong>
                . You will see each consent you have given, labelled by purpose,
                with a toggle to withdraw. Withdrawal takes effect immediately
                for future processing. It does not affect data already
                processed, and does not apply to data we must retain by law.
              </p>
              <p>
                Withdrawing consent for a core feature (e.g. storing your
                prescription) will mean we can no longer provide that feature.
                We will show you this impact before you confirm withdrawal.
              </p>
              <p>
                To submit a formal data request (access, correction, or
                erasure), write to{" "}
                <a href="mailto:info@healthismplus.com">
                  info@healthismplus.com
                </a>{" "}
                with the subject line "Data Request — [Your Name]". We will
                respond within 7 business days.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 12 */}
            <section className="policy-section" id="children">
              <p className="section-number">Section 12</p>
              <h2>Children and minors</h2>
              <p>
                The HealthismPlus account holder must be 18 or above. Parents
                and legal guardians may create and manage a dependent profile
                for a minor (for example, for pediatric bookings or storing a
                child's health records) under their own account, with verifiable
                parental consent given at the time the dependent profile is
                created. We do not use a minor's data for tracking, behavioural
                monitoring, or targeted advertising.
              </p>
              <p>
                If you are a parent or guardian and believe your child's data
                has been collected without your consent, please contact us at{" "}
                <a href="mailto:info@healthismplus.com">
                  info@healthismplus.com
                </a>{" "}
                and we will delete the relevant data promptly.
              </p>
              <p>
                In the case of HMS, patient records for minors are handled under
                the Healthcare Provider's guardianship and consent protocols,
                consistent with applicable state Clinical Establishment
                regulations and NMC professional conduct norms.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 13 */}
            <section className="policy-section" id="security">
              <p className="section-number">Section 13</p>
              <h2>How we protect your data</h2>
              <p>
                We implement security measures appropriate to the sensitivity of
                health data, including:
              </p>
              <p>
                → Encryption of all health data in transit (TLS 1.2+) and at
                rest (AES-256).
              </p>
              <p>
                → Role-based access controls so only authorised personnel and
                systems can access your data.
              </p>
              <p>→ Regular security audits and vulnerability assessments.</p>
              <p>
                → Data breach response procedures: if a breach occurs that is
                likely to affect your rights, we will notify you and the Data
                Protection Board of India as required under the DPDP Act.
              </p>
              <p>
                No system is completely secure. If you suspect unauthorised
                access to your account, please contact us immediately at{" "}
                <a href="mailto:info@healthismplus.com">
                  info@healthismplus.com
                </a>
                .
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 14 */}
            <section className="policy-section" id="changes">
              <p className="section-number">Section 14</p>
              <h2>Changes to this policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we
                make material changes — those that affect your rights or how we
                use your data — we will notify you through the app and by email
                at least 14 days before the changes take effect, and obtain
                fresh consent where required.
              </p>
              <p>
                The "Last updated" date at the top of this page will always
                reflect the most recent version. We keep previous versions
                archived at{" "}
                <Link to="/privacy-policy/archive">
                  www.healthismplus.com/privacy-policy/archive
                </Link>{" "}
                for your reference.
              </p>
            </section>

            <hr className="section-rule" />

            {/* SECTION 15 */}
            <section className="policy-section" id="contact">
              <p className="section-number">Section 15</p>
              <h2>Contact us &amp; grievance redressal</h2>
              <p>
                If you have any questions about this policy, want to exercise a
                data right, or wish to raise a complaint, please reach out
                through the channels below.
              </p>

              <div className="contact-card">
                <div className="contact-block">
                  <p className="label">Grievance Officer</p>
                  <strong>Kashyap Thakkar</strong>
                  <span style={{ color: "#3D5044", fontSize: "12px" }}>
                    Founder &amp; CEO
                  </span>
                  <a href="mailto:info@healthismplus.com">
                    info@healthismplus.com
                  </a>
                  <span style={{ color: "#6B7C70", fontSize: "12px" }}>
                    Response within 7 business days
                  </span>
                </div>
                <div className="contact-block">
                  <p className="label">General Support</p>
                  <strong>HealthismPlus Support</strong>
                  <a href="mailto:info@healthismplus.com">
                    info@healthismplus.com
                  </a>
                  <span style={{ color: "#6B7C70", fontSize: "12px" }}>
                    For account and booking queries
                  </span>
                </div>
                <div className="contact-block">
                  <p className="label">Registered Office</p>
                  <strong>Healthismplus Technologies Private Limited</strong>
                  <span>
                    405 Garnet Palladium, Behind Express Zone, Motilal Nehru
                    Road, Goregaon East, Mumbai – 400063, Maharashtra, India
                  </span>
                </div>
                <div className="contact-block">
                  <p className="label">Data Protection Board</p>
                  <strong>Escalation</strong>
                  <span>
                    If you are not satisfied with our response, you may escalate
                    to the Data Protection Board of India at{" "}
                    <strong>meity.gov.in</strong>
                  </span>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer onSectionClick={scrollToSection} setActiveTab={setActiveTab} />
    </div>
  );
};

export default PrivacyPolicy;
